import React, { useState, useEffect } from 'react'

import {
  formatCurrency,
  formatNumber,
  copy,
  sortAttractions,
} from '../../../utilities/helpers'

import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'

import { InfoIcon } from '../../InfoIcon'
import DashboardPricingLevelMatrix from '../../PricingLevelPerformanceWrapper/DashboardPricingLevelMatrix'

import placeholder from '../../../assets/placeholder.png'

import './cards.scss'

function offerActivity(eventOffers, offerSales) {
  const byName = new Map()

  for (const offer of eventOffers || []) {
    const name = String(offer?.name || '').trim()
    if (!name || byName.has(name)) continue
    byName.set(name, { name, net: 0 })
  }

  for (const row of offerSales || []) {
    const name = String(row?.name || '').trim()
    if (!name) continue
    const current = byName.get(name) || { name, net: 0 }
    current.net = Number(row.net) || 0
    byName.set(name, current)
  }

  return [...byName.values()].sort(
    (a, b) => b.net - a.net || a.name.localeCompare(b.name)
  )
}

function mixCounts(total, complimentary) {
  const safeTotal = Math.max(0, Number(total) || 0)
  const comp = Math.min(safeTotal, Math.max(0, Number(complimentary) || 0))
  return { primary: safeTotal - comp, comp }
}

function withPercents(segments) {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0)
  if (!total) return null

  let lastIndex = -1
  for (let index = segments.length - 1; index >= 0; index -= 1) {
    if (segments[index].value > 0) {
      lastIndex = index
      break
    }
  }
  let assigned = 0

  return segments.map((segment, index) => {
    if (segment.value <= 0) return { ...segment, percent: 0 }
    if (index === lastIndex) return { ...segment, percent: 100 - assigned }
    const percent = Math.round((segment.value / total) * 100)
    assigned += percent
    return { ...segment, percent }
  })
}

function channelComparison(salesMetrics) {
  const channels = salesMetrics?.channelTickets
  if (!channels) return null

  const website = Math.max(0, Number(channels.website) || 0)
  const assignments = Math.max(0, Number(channels.ticketAssignment) || 0)
  const boxOffice = Math.max(0, Number(channels.boxOffice) || 0)
  const packages = Math.max(0, Number(channels.packages) || 0)
  const segments = [
    { label: 'Website', value: website, tone: 'website' },
    { label: 'Assignments', value: assignments, tone: 'assignment' },
  ]
  if (boxOffice > 0) {
    segments.push({ label: 'Box office', value: boxOffice, tone: 'box-office' })
  }
  if (packages > 0) {
    segments.push({ label: 'Packages', value: packages, tone: 'packages' })
  }

  return {
    label:
      boxOffice > 0 || packages > 0 ? 'By channel' : 'Website vs. assignments',
    segments,
  }
}

function ComparisonRow({ label, segments, testId, formatValue }) {
  const rows = withPercents(segments)
  if (!rows) return null

  const visible = rows.filter((segment) => segment.value > 0)
  const lead = visible[0]

  return (
    <div
      className="ticket-compare"
      data-testid={testId}
      role="img"
      aria-label={visible
        .map((segment) => `${formatValue(segment.value)} ${segment.label}`)
        .join(', ')}
    >
      <div className="ticket-compare__heading">
        <span className="small-label mb-0">{label}</span>
        <span className="ticket-compare__stats">
          {visible.map((segment) => (
            <span key={segment.label}>
              <strong>{formatValue(segment.value)}</strong>{' '}
              {segment.label.toLowerCase()}
            </span>
          ))}
          {lead && <span>{lead.percent}%</span>}
        </span>
      </div>
      <div className="ticket-mix__track">
        {visible.map((segment) => (
          <span
            key={segment.label}
            className={`ticket-mix__fill ticket-mix__fill--${segment.tone}`}
            style={{ flexGrow: segment.value }}
          />
        ))}
      </div>
    </div>
  )
}

function InfoTip({ children, label, placement = 'right' }) {
  return (
    <OverlayTrigger
      placement={placement}
      overlay={<Tooltip>{children}</Tooltip>}
    >
      <Button variant="link" aria-label={label}>
        <InfoIcon variant="dark" />
      </Button>
    </OverlayTrigger>
  )
}

export default function Cards({
  sharableLink,
  stats,
  handleShow,
  isVisible,
  isPublished,
  handleVisibilityChange,
  isDisabled,
  salesMetrics,
  isRefreshing,
  onRefresh,
}) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false)
      }, 2000)
    }
  }, [copied])

  const formatMetric = (value, format) =>
    value === null || value === undefined ? '—' : format(value)

  const soldMix = mixCounts(
    salesMetrics?.ticketsSold,
    salesMetrics?.complimentarySold
  )
  const scanMix = mixCounts(
    salesMetrics?.scanned,
    salesMetrics?.complimentaryScanned
  )
  const offers = salesMetrics
    ? offerActivity(stats?.offers, salesMetrics.offerSales)
    : null
  const offerTotal = offers
    ? offers.reduce((sum, offer) => sum + offer.net, 0)
    : 0
  const channels = channelComparison(salesMetrics)
  const forecast = stats?.weatherForecast
  const carts = (() => {
    const cartActivity = stats?.cartActivity
    if (!cartActivity) return null
    const added = Math.max(0, Number(cartActivity.total_sessions) || 0)
    const checkedOut = Math.max(0, Number(cartActivity.completed_sessions) || 0)
    const dropped = Math.max(0, Number(cartActivity.total_abandoned_count) || 0)
    return {
      added,
      checkedOut,
      dropped,
      stillOpen: Math.max(0, added - checkedOut - dropped),
    }
  })()

  return (
    <section className="cards">
      <Row>
        <Col xs={8} className="col-1 pe-3">
          <Row>
            <Col>
              <Card body className="card--light">
                <Stack
                  direction="horizontal"
                  className="justify-content-between align-items-center mb-3"
                >
                  <Stack direction="horizontal" className="small-label--flex">
                    <h1 className="card-title mb-0">Ticket activity</h1>
                    <InfoTip label="About ticket activity">
                      Current ticket sales and entry scans
                    </InfoTip>
                  </Stack>
                  <Button
                    variant="outline-light"
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    data-testid="dashboard-metrics-refresh"
                  >
                    {isRefreshing ? 'Refreshing…' : 'Refresh'}
                  </Button>
                </Stack>
                <Row className="dashboard-metrics">
                  <Col>
                    <span className="small-label">Tickets allocated</span>
                    <h1
                      className="card-title card-title-lg mb-0"
                      data-testid="tickets-sold"
                    >
                      {formatMetric(salesMetrics?.ticketsSold, formatNumber)}
                    </h1>
                  </Col>
                  <Col>
                    <Stack
                      direction="horizontal"
                      className="small-label--flex align-items-center"
                    >
                      <span className="small-label">Ticket proceeds</span>
                      <InfoTip label="About ticket proceeds" placement="top">
                        This is the net from ticket sales
                      </InfoTip>
                    </Stack>
                    <h1
                      className="card-title card-title-lg mb-0"
                      data-testid="ticket-sales"
                    >
                      {formatMetric(
                        salesMetrics?.ticketProceeds,
                        formatCurrency
                      )}
                    </h1>
                  </Col>
                  <Col>
                    <span className="small-label">Tickets scanned</span>
                    <h1
                      className="card-title card-title-lg mb-0"
                      data-testid="tickets-scanned"
                    >
                      {formatMetric(salesMetrics?.scanned, formatNumber)}
                    </h1>
                    {salesMetrics?.ticketsSold != null && (
                      <span
                        className="subtitle subtitle--dark"
                        data-testid="tickets-scanned-of"
                      >
                        of {formatNumber(salesMetrics.ticketsSold)}
                      </span>
                    )}
                  </Col>
                </Row>
                {salesMetrics && (
                  <div className="ticket-comparisons">
                    <ComparisonRow
                      label="Sold vs. comp"
                      testId="tickets-sold-mix"
                      formatValue={formatNumber}
                      segments={[
                        { label: 'Sold', value: soldMix.primary, tone: 'sold' },
                        { label: 'Comp', value: soldMix.comp, tone: 'comp' },
                      ]}
                    />
                    {channels && (
                      <ComparisonRow
                        label={channels.label}
                        testId="website-assignments-mix"
                        formatValue={formatNumber}
                        segments={channels.segments}
                      />
                    )}
                    {scanMix.primary + scanMix.comp > 0 && (
                      <ComparisonRow
                        label="Paid vs Comp Scans"
                        testId="paid-comp-scans"
                        formatValue={formatNumber}
                        segments={[
                          {
                            label: 'Paid',
                            value: scanMix.primary,
                            tone: 'sold',
                          },
                          { label: 'Comp', value: scanMix.comp, tone: 'comp' },
                        ]}
                      />
                    )}
                  </div>
                )}
              </Card>
            </Col>
          </Row>
          <Row>
            <Col>
              <Card body className="card--light">
                <Stack direction="horizontal" className="small-label--flex">
                  <h1 className="card-title mb-0">Cart activity</h1>
                  <InfoTip label="About cart activity">
                    Carts started for this event
                  </InfoTip>
                </Stack>
                <Row className="dashboard-metrics mt-3">
                  <Col>
                    <div className="cart-metric cart-metric--added">
                      <span className="small-label">Added to cart</span>
                      <h1
                        className="card-title card-title-lg mb-0"
                        data-testid="carts-added"
                      >
                        {formatMetric(carts?.added, formatNumber)}
                      </h1>
                    </div>
                  </Col>
                  <Col>
                    <div className="cart-metric cart-metric--checked-out">
                      <span className="small-label">Checked out</span>
                      <h1
                        className="card-title card-title-lg mb-0"
                        data-testid="carts-checked-out"
                      >
                        {formatMetric(carts?.checkedOut, formatNumber)}
                      </h1>
                    </div>
                  </Col>
                  <Col>
                    <div className="cart-metric cart-metric--dropped">
                      <span className="small-label">Dropped</span>
                      <h1
                        className="card-title card-title-lg mb-0"
                        data-testid="carts-dropped"
                      >
                        {formatMetric(carts?.dropped, formatNumber)}
                      </h1>
                    </div>
                  </Col>
                  <Col>
                    <div className="cart-metric cart-metric--open">
                      <span className="small-label">Still open</span>
                      <h1
                        className="card-title card-title-lg mb-0"
                        data-testid="carts-open"
                      >
                        {formatMetric(carts?.stillOpen, formatNumber)}
                      </h1>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col>
              <Card body className="card--light">
                <Stack
                  direction="horizontal"
                  className="small-label--flex align-items-center"
                >
                  <h1 className="card-title mb-0">Sales by offer</h1>
                  <InfoTip label="About sales by offer" placement="top">
                    Only tickets with an offer name are included. The amount is
                    net ticket proceeds.
                  </InfoTip>
                </Stack>
                <p className="small text-muted mb-0 mt-1">
                  Source: Website, Boxoffice
                </p>
                {offers ? (
                  <>
                    <ul className="offer-sales" data-testid="offer-sales">
                      {offers.length ? (
                        offers.map((offer) => (
                          <li className="offer-sales__row" key={offer.name}>
                            <span className="offer-sales__name">
                              {offer.name}
                            </span>
                            <span
                              className="offer-sales__net"
                              data-testid={`offer-net-${offer.name}`}
                            >
                              {formatCurrency(offer.net)}
                            </span>
                          </li>
                        ))
                      ) : (
                        <li className="offer-sales__empty">No offers yet</li>
                      )}
                    </ul>
                    {offers.length > 0 && (
                      <div
                        className="offer-sales__total"
                        data-testid="offer-sales-total"
                      >
                        <span>Total</span>
                        <span>{formatCurrency(offerTotal)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <h1
                    className="card-title card-title-lg mb-0 mt-3"
                    data-testid="offer-sales-unavailable"
                  >
                    —
                  </h1>
                )}
              </Card>
            </Col>
          </Row>
          {stats?.organizationUUID && stats?.uuid && (
            <Row>
              <Col>
                <DashboardPricingLevelMatrix
                  organizationUUID={stats.organizationUUID}
                  eventUUID={stats.uuid}
                  isRefreshing={isRefreshing}
                />
              </Col>
            </Row>
          )}
        </Col>
        <Col xs={4} className="col-2 ps-3">
          <div className="dashboard-aside">
            <Card className="card--md card--light">
              <Card.Body>
                <div className="event-image-frame">
                  <img
                    src={stats?.image?.url || placeholder}
                    alt={`Cover art for ${stats?.name} event`}
                    className="event-image"
                  />
                </div>
              </Card.Body>
            </Card>
            {stats?.attractions?.length > 0 && (
              <Card body className="card--light">
                <Stack
                  direction="horizontal"
                  className="small-label--flex mb-3"
                >
                  <h1 className="card-title mb-0">Attractions</h1>
                  <InfoTip label="About attractions">
                    Teams and performers on this event
                  </InfoTip>
                </Stack>
                <Stack as="ul" gap={2}>
                  {sortAttractions(stats?.attractions)?.map(
                    (attraction, index) => (
                      <Stack as="li" direction="horizontal" key={index}>
                        <img
                          src={attraction?.artwork?.url || placeholder}
                          width="40"
                          height="40"
                          alt={`Portrait of ${attraction.name}`}
                          className="rounded-circle me-3"
                        />
                        <span className="small small-bold">
                          {attraction.name}
                        </span>
                      </Stack>
                    )
                  )}
                </Stack>
              </Card>
            )}
            {forecast?.recommendedAction && (
              <Card
                body
                className="card--sm background-primary"
                data-testid="weather-recommended-action"
              >
                <Stack direction="horizontal" className="small-label--flex">
                  <span className="small-label">Recommended action</span>
                  <InfoTip label="About this recommendation">
                    Suggested from the weather forecast for this event
                  </InfoTip>
                </Stack>
                <p className="mb-0 mt-2">{forecast.recommendedAction}</p>
              </Card>
            )}
            <Card body className="card--light card--md-vertical">
              <div className="card-body-heading page-views heading--flex mb-0">
                <Stack>
                  <Stack direction="horizontal" className="small-label--flex">
                    <span className="small-label">Page views</span>
                    <InfoTip label="About page views">
                      How many times your event URL has been viewed
                    </InfoTip>
                  </Stack>
                  <h1 className="card-title card-title-lg">
                    {formatNumber(stats?.statSheet?.pageViews)}
                  </h1>
                  <span className="subtitle subtitle--dark">
                    Visitors are finding this event
                  </span>
                </Stack>
              </div>
            </Card>
            <Card body className="card--light card--md-vertical">
              <div className="card-body-heading heading--flex shareable-link mb-0">
                <Stack direction="horizontal" className="small-label--flex">
                  <h1 className="card-title mb-0">Event link & QR</h1>
                  <InfoTip label="About the event link">
                    Share this link or open the QR code for the event
                  </InfoTip>
                </Stack>
              </div>
              {sharableLink && (
                <p className="subtitle subtitle--dark text-truncate mt-3 mb-0">
                  {sharableLink}
                </p>
              )}
              <Stack direction="horizontal" gap={2} className="mt-3">
                <Button
                  variant="outline-light"
                  className={`copy-link-btn btn-width-xs--flex-item ${
                    copied ? 'btn-copy--success' : ''
                  } `}
                  onClick={() => copy(sharableLink, setCopied)}
                  disabled={isDisabled}
                >
                  {!copied && 'Copy link'}
                </Button>
                <Button
                  onClick={handleShow}
                  variant="outline-light"
                  disabled={isDisabled}
                >
                  Download QR code
                </Button>
              </Stack>
            </Card>
            <Card
              body
              id="visibility-card"
              className="card--sm card-with-border"
            >
              <Form.Check
                type="switch"
                id="event-visibility"
                label="Visible on website"
                checked={isVisible}
                onChange={(e) => {
                  handleVisibilityChange(e, e.target.checked)
                }}
                disabled={isDisabled || !isPublished}
                className="form-switch--with-desc gap-lg"
              />
              <small className="subtitle subtitle--dark form-switch--with-desc-subtitle gap-lg">
                {isPublished
                  ? isVisible
                    ? 'Event is live and bookable.'
                    : 'Event is hidden from attendees.'
                  : 'Event must be published to toggle visibility'}
              </small>
            </Card>
          </div>
        </Col>
      </Row>
    </section>
  )
}
